import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AccountService } from '../../account/account.service';
import { GeraetTyp, UserSession } from '../../models/user-session.model';
import { SectionTitleComponent } from '../section-title/section-title.component';
import { StatusPillComponent } from '../status-pill/status-pill.component';

type Zustand = 'laden' | 'fehler' | 'bereit';

const MINUTE = 60_000;
const STUNDE = 60 * MINUTE;
const TAG = 24 * STUNDE;

@Component({
  selector: 'app-aktive-sitzungen',
  standalone: true,
  imports: [TranslatePipe, MatButtonModule, MatIconModule, SectionTitleComponent, StatusPillComponent],
  templateUrl: './aktive-sitzungen.component.html',
  styleUrl: './aktive-sitzungen.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AktiveSitzungenComponent implements OnInit {
  private readonly accountService = inject(AccountService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  readonly zustand = signal<Zustand>('laden');
  readonly sitzungen = signal<UserSession[]>([]);
  readonly abmeldend = signal<string | null>(null);

  ngOnInit(): void {
    this.laden();
  }

  laden(): void {
    this.zustand.set('laden');
    this.accountService
      .listSessions()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (sitzungen) => {
          this.sitzungen.set(sitzungen);
          this.zustand.set('bereit');
        },
        error: () => this.zustand.set('fehler'),
      });
  }

  abmelden(sitzung: UserSession): void {
    this.abmeldend.set(sitzung.id);
    this.accountService
      .revokeSession(sitzung.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.sitzungen.update((liste) => liste.filter((s) => s.id !== sitzung.id));
          this.abmeldend.set(null);
        },
        error: () => {
          this.abmeldend.set(null);
          this.snackBar.open(this.translate.instant('aktiveSitzungen.abmeldenFehler'), undefined, {
            panelClass: 'snackbar-error',
          });
        },
      });
  }

  icon(sitzung: UserSession): GeraetTyp {
    return sitzung.geraetTyp === 'smartphone' ? 'smartphone' : 'computer';
  }

  geraetName(sitzung: UserSession): string {
    return sitzung.geraet ?? this.translate.instant('aktiveSitzungen.unbekanntesGeraet');
  }

  relativeZeit(iso: string): string {
    const abstand = Date.now() - Date.parse(iso);
    if (abstand < MINUTE) return this.translate.instant('aktiveSitzungen.jetzt');
    if (abstand < STUNDE) return this.translate.instant('aktiveSitzungen.minuten', { n: Math.floor(abstand / MINUTE) });
    if (abstand < TAG) return this.translate.instant('aktiveSitzungen.stunden', { n: Math.floor(abstand / STUNDE) });
    const tage = Math.floor(abstand / TAG);
    return tage === 1
      ? this.translate.instant('aktiveSitzungen.tag')
      : this.translate.instant('aktiveSitzungen.tage', { n: tage });
  }
}
